import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Camera, CameraResultType, Photo } from '@capacitor/camera';
import { Upload } from 'src/app/core/data/schema/upload';
import { ContentPreviewComponent } from '../content-preview/content-preview.component';
import { ServiceHelper } from '../../helper/services.helper';
import { Utilities } from '../../helper/util';
import { Capacitor } from '@capacitor/core';
import { Log } from '../../log';

@Component({
  selector: 'app-content-upload',
  templateUrl: './content-upload.component.html',
  styleUrls: ['./content-upload.component.scss'],
})
export class ContentUploadComponent implements OnInit, OnChanges {

  @ViewChild('viewer', { static: true }) contentViewer: ContentPreviewComponent;
  @Output() fileSelected: EventEmitter<Upload> = new EventEmitter();
  @Output() filesSelected: EventEmitter<Upload[]> = new EventEmitter();
  @Output() fileRemoved: EventEmitter<Upload> = new EventEmitter();

  @Input() uploadTitle = 'Select File';
  @Input() uploadDetails;
  @Input() promptForDelete = false;
  @Input() placeHolderImg = '';
  @Input() showUploadButtonText = true;
  @Input() files: Upload[] = [];
  @Input() config: IUploadConfig = {
    maxFiles: 1,
    allowChange: true,
    showPreview: true,
    acceptedTypes: 'image/*',
    previewHeight: 150,
    maxUploadFiles: 10
  };
  @Input() disabled = false;

  filesRemaining = 0;
  id: string;
  lastFile: Upload = new Upload();
  libraryShown = false;

  constructor(private sanitizer: DomSanitizer) { }

  get isCordova() { return Capacitor.isNativePlatform(); }
  get imageFilesOnly() { return this.config.acceptedTypes.includes('image/*'); }

  ngOnInit() {
    this.id = this.getuploadId();
    const validFiles = [];

    // if files aren't an array, make them an array
    if (!Array.isArray(this.files)) {
      this.files = [this.files];
    }

    this.files.forEach(f => { if (f != null) { validFiles.push(f); }; });
    this.files = validFiles;
    this.filesRemaining = (this.config.maxFiles - this.files.length);

  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.config.showPreview === false && this.files.length === 1) {
      this.lastFile = this.files[0];
    }
    if (this.files == null) {
      this.reset();
    } else if (!Array.isArray(this.files) && (this.files as Upload).path == null) {
      this.reset();
    }
  }

  reset() {
    this.filesRemaining = this.config.maxFiles;
    this.files = [];
  }

  async removeFile(file: Upload) {
    if (this.promptForDelete && !(await ServiceHelper.confirmSomething('Delete Image?', 'Are you sure you want to delete this image?', 'No', 'Yes') === true)) {
      return;
    }
    this.fileRemoved.emit(file);
    this.filesRemaining++;

    // Remove the file if needed
    const idOf = this.files.indexOf(file);
    if (idOf > -1) {
      this.files.splice(idOf, 1);
    }

    this.lastFile = new Upload();
  }

  onBrowse() {
    if (this.isCordova) {
      this.showLibrary();
    } else {
      this.triggerBrowse();
    }
  }

  triggerBrowse() {
    this.libraryShown = false;
    document.getElementById(this.id).click();
  }

  showLibrary() {
    this.libraryShown = !this.libraryShown;
  }

  async selectFile(selFile: any) {

    if (!this.canAddMoreMedia) {
      return;
    }

    // Verify we can add pictures (give warnings)
    if (selFile === null || selFile.target.files.length === 0) {
      return;
    }

    let validFiles: { file: File, path: string }[] = [];
    for (const file of selFile.target.files) {

      // If the picker is for images, then ensure the file is an image
      if (this.imageFilesOnly && !this.isImage(file.name)) {
        const alert = await ServiceHelper.displayAlert('Unsupported file type', `${file.name} could not be uploaded. Only image files are allowed.`);
        await alert.onDidDismiss();
        continue;
      }

      let maxSize = 100;
      if (this.imageFilesOnly || this.isImage(file.name)) {
        maxSize = 10;
      }

      const blob = file as Blob;
      const sizeInMb = Math.floor((blob.size / 1000) / 1000);
      if (sizeInMb > maxSize) {
        const alert = await ServiceHelper.displayAlert('File too large', `${file.name} exceeds the ${maxSize} MB limit and could not be uploaded.`);
        await alert.onDidDismiss();
        continue;
      }

      try {
        let path = '';
        if (this.isImage(file.name, true)) {
          path = await Utilities.resizeImage(blob, 1280, 720) as string;
        } else {
          path = URL.createObjectURL(file);
        }

        validFiles.push({ file, path });
      } catch (error) {
        const alert = await ServiceHelper.displayAlert('Error Processing File', `${file.name} could not be uploaded. An error occurred while processing the file.`);
        await alert.onDidDismiss();
        continue;
      }
    }

    // sort the files by name
    validFiles = validFiles.sort((a, b) => a.file.name.localeCompare(b.file.name));

    // limit to X selected files
    if (validFiles.length > this.config.maxUploadFiles || this.files.length + validFiles.length > this.config.maxFiles) {

      let max = this.config.maxUploadFiles;
      if (this.files.length + validFiles.length > this.config.maxFiles) {
        max = this.config.maxFiles - this.files.length;
      }
      max = Math.min(max, validFiles.length);

      let message = `Only ${max} ${this.imageFilesOnly ? 'images' : 'files'} can be uploaded at a time.`;
      const alert = await ServiceHelper.displayAlert('Upload limit reached', message);
      await alert.onDidDismiss();

      validFiles = validFiles.slice(0, max);
    }

    // now prepare the files for upload
    Log.debug(`Preparing ${validFiles.length} files for upload`);
    const processedFiles = [];
    for (const file of validFiles) {
      this.prepareForUpload(file.path, file.file.name, file.file.type);
      processedFiles.push(this.lastFile);
    }

    this.filesSelected.emit(processedFiles);

    // reset input value so we can reselect the same file
    selFile.target.value = null;

  }

  async takePicture() {
    if (!this.canAddMoreMedia) {
      return;
    }

    // Continue on
    this.libraryShown = false;

    try {
      // Turn the image into base64 and add to the pending
      const image = await Camera.getPhoto({
        quality: 75,
        correctOrientation: true,
        height: 720,
        width: 1280,
        allowEditing: false, // still kind of buggy
        resultType: CameraResultType.Uri
      }) as Photo;

      // convert the base 64 into a data url
      // const imgUrl = URL.createObjectURL();
      this.prepareForUpload(image.webPath, null, 'image/jpeg');
    } catch (error) {
      ServiceHelper.displayAlert(
        'Camera Error',
        (error === 'cordova_not_available' ? 'This option is only available on device with camera' : error)
      );
    }
  }

  private isImage(fileName: string, resizableOnly: boolean = false) {
    let images: string[] = ['jpg', 'bmp', 'png', 'svg', 'jpeg'];
    if (resizableOnly === false) {
      images.push('webp', 'gif', 'xbm', 'tif', 'jfif', 'pjp', 'apng', 'svgz', 'heic', 'heif', 'avif', 'ico', 'tiff', 'pjpeg');
    }
    const split = fileName.split('.');
    const extension = split[split.length - 1].toLowerCase();
    return (images.indexOf(extension) > -1);
  }



  private prepareForUpload(path: string, name: string, type: string) {
    // If it's capacitor, then we need to make a proper URL based on the one created above
    if (this.isCordova) {
      const webView = (window as any).Ionic.WebView;
      path = webView.convertFileSrc(path);
    }

    const safeUrl = this.sanitizer.bypassSecurityTrustUrl(path);
    this.lastFile = new Upload(path);
    this.lastFile.safeUrl = safeUrl;
    this.lastFile.mimeType = type;
    this.lastFile.thumbPath = safeUrl;
    this.lastFile.filename = name;
    this.lastFile.testType();
    this.fileLoaded(this.lastFile);
  }

  private canAddMoreMedia() {
    if (this.filesRemaining === 0 || this.config.allowChange) {
      ServiceHelper.displayAlert('Too Many Files', `You can only select up to ${this.config.maxFiles} files.`);
      return false;
    }
    return true;
  }

  private fileLoaded(file: Upload) {
    // emit that we loaded it
    this.fileSelected.emit(file);
    if (this.files.length + 1 > this.config.maxFiles) {
      this.files.shift(); // remove the first item to allow new item to be added
    }
    this.files.push(file);
    this.files = Object.assign([], this.files);
    this.filesRemaining--;
    if (this.config.showPreview) {
      this.contentViewer.imageAdded();
    }
  }

  private getuploadId() {
    let text = '';
    const possible = 'abcdefghijklmnopqrstuvwxyz1234679';
    for (let i = 0; i < 3; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }

}

export interface IUploadConfig {
  showPreview?: boolean;
  maxFiles?: number;
  maxUploadFiles?: number;
  allowVideoCapture?: boolean;
  allowChange?: boolean;
  fields?: string[];
  acceptedTypes?: string;
  showThumbnails?: boolean;
  allowRemove?: boolean;
  previewHeight?: number;
  thumbSize?: number;
  mode?: string;
}
